import {
  Checkbox,
  CheckboxProps,
  Flex,
} from 'antd'
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react'

import GroupTree from '@/components/fields/group-tree'
import { NormalStatus } from '@/constants/normal-status'
import { GroupType } from '@/model/group'
import {
  difference,
  getAllChecked,
  getIsIndeterminate,
  union,
} from '@/utils/array'

import s from './group-tree-with-checkbox-list.module.css'

export interface GroupTreeWithCheckboxListProps {
  type: GroupType
  value?: number[]
  options: {
    value: number
    label: string
    status: NormalStatus
    groupIds: number[]
  }[]
  onChange?: (value?: number[]) => void
}

export default function GroupTreeWithCheckboxList({
  type,
  value,
  options,
  onChange,
}: GroupTreeWithCheckboxListProps) {
  const [selectedGroupId, setSelectedGroupId] = useState<number>()

  const availableOptions = useMemo(() => {
    return options.filter(option => {
      if (selectedGroupId && !option.groupIds.includes(selectedGroupId)) {
        return false
      }
      if (value?.includes(option.value)) {
        return true
      }
      if (option.status === NormalStatus.Deactivated) {
        return false
      }
      return true
    })
  }, [options, selectedGroupId, value])

  const allValues = availableOptions.map(({ value }) => value)

  const onChangeAll: NonNullable<CheckboxProps['onChange']> = useCallback(({ target: { checked } }) => {
    if (onChange) {
      if (checked) {
        onChange(union(value, allValues))
      } else {
        onChange(difference(value, allValues))
      }
    }
  }, [allValues, onChange, value])
  const onChangeOne = (id: number): NonNullable<CheckboxProps['onChange']> => ({ target: { checked } }) => {
    if (onChange) {
      if (checked) {
        onChange(union(value, [id]))
      } else {
        onChange(difference(value, [id]))
      }
    }
  }

  return (
    <Flex className={s.Root}>
      <GroupTree type={type} onChange={setSelectedGroupId} value={selectedGroupId} />
      <div className={s.CheckboxList}>
        <Checkbox
          indeterminate={getIsIndeterminate(value, allValues)}
          onChange={onChangeAll}
          checked={getAllChecked(value, allValues)}
        >
          All
        </Checkbox>
        {availableOptions
          .map((option) => (
            <Checkbox
              key={option.value}
              onChange={onChangeOne(option.value)}
              checked={value?.includes(option.value)}
            >
              {option.label}
            </Checkbox>
          ))}
      </div>
    </Flex>
  )
}
